import { define, isDefined, round } from "../../../utils/typeUtils"
import { TableName } from "../../../tables"
import { FieldType, FieldValue, FormatType } from "../../ui"
import { CodedDataType, Currency, ListProp, NamedDataType } from "../../../tables/types/types"
import { useFetchActions, useLoaders } from "../../../modules/common"
import { dedupe, getNextFreeNumber } from "../../../utils/listUtils"
import { IdentifierType, useSystemStateHooks } from "../../../modules/system"
import { identifierType } from "../utils/dataTypesUtils"

export const useDataTypesHooks = () => {
    const { companyId } = useSystemStateHooks()

    const loaders = useLoaders()
    const fetchActions = useFetchActions()

    const generateCode = async (
        table?: TableName,
        additonalItems: CodedDataType[] = []
    ) => {
        const { fetchAllProps } = fetchActions(companyId)
        const list = table ? await fetchAllProps(table, 'code') : []
        const codes = list
            .map(item => (item as CodedDataType).code)
            .concat(additonalItems.map(item => item.code))
        return getNextFreeNumber(dedupe(codes))
    }

    const generatePropCode = (dataType: ListProp, propName: string) => {
        const list = dataType[propName as keyof ListProp] as object[]
        const codes = list.map(item => (item as CodedDataType).code)
        return getNextFreeNumber(codes)
    }

    const formatValue = (
        value?: FieldValue,
        type: FieldType = FieldType.TEXT
    ): string => {
        let result: string = ''
        
        if (!isDefined(value)) {
            result = '-'
        } else if (type === FieldType.TEXT) {
            result = value as string
        } else if (type === FieldType.NUMBER) {
            result = formatNumber(value as number)
        } else if (type === FieldType.PRICE) {
            result = formatPrice(value as number)
        } else if (type === FieldType.PERCENTAGE) {
            result = formatPercentage(value as number)
        } else if (type === FieldType.DATE) {
            result = formatDate(value as Date)
        } else if (type === FieldType.BOOLEAN) {
            result = formatBoolean(!!value)
        }
    
        return result
    }

    const formatNumber = (value?: number) => isDefined(value) ? Intl.NumberFormat('es-AR').format(round(value!)) : ''

    const formatPrice = (value?: number) => isDefined(value) ? Intl.NumberFormat('es-AR', { style: 'currency', currency: Currency.ARS }).format(value!) : ''

    const formatDate = (value?: Date, separator = '/') => value ? `${value.getDate()}${separator}${value.getMonth()+1}${separator}${value.getFullYear()}` : ''
    
    const formatTime = (value?: Date) => value ? `${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}` : ''
    
    const formatDateTime = (value?: Date) => value ? `${formatDate(value)} ${formatTime(value)}` : ''

    const formatPercentage = (value?: number) => isDefined(value) ? `${value! >= 0 ? '+' : ''}${formatNumber(value)} %` : ''

    const formatBoolean = (value: boolean) => value ? 'Sí' : 'No'

    const formatTable = async (table: TableName, id: string) => {
        const { load } = loaders()
        const dataType = (await load(table, id))! as NamedDataType
        return dataType.name || dataType.id
    }

    const formatCustomValue = (
        value?: FieldValue,
        formatType: FormatType = FormatType.DNI
    ) => {
        let result: string = ''

        if (!isDefined(value)) {
            result = ''
        } else if (formatType === FormatType.DNI) {
            result = formatValue(value, FieldType.NUMBER)
        } else if (formatType === FormatType.CUIT_CUIL) {
            const cuitOrCuil = define(value?.toString())
            const part1 = cuitOrCuil.slice(0, 2)
            const part2 = cuitOrCuil.slice(2, 4)
            const part3 = cuitOrCuil.slice(4, 7)
            const part4 = cuitOrCuil.slice(7, 10)
            const part5 = cuitOrCuil.slice(10, 11)
            
            result = `${part1}-${part2}.${part3}.${part4}-${part5}`
        } else if (formatType === FormatType.PHONE) {
            const phone = define(value?.toString())
            let parts: string[] = []
            let currentPart = ''
            for (let i = phone.length - 1; i >= 0; i--) {
                currentPart = phone[i] + currentPart
                if (currentPart.length === 4) {
                    parts.unshift(currentPart)
                    currentPart = ''
                }
            }
            if (currentPart.length > 0) {
                parts.unshift(currentPart)
            }
            
            result = parts.join('-')
        } else if (formatType === FormatType.PLATE) {
            const phone = value! as string
            if (phone.length === 6) {
                result = `${phone.slice(0, 3)}-${phone.slice(3, 6)}`
            } else if (phone.length === 7) {
                result = `${phone.slice(0, 2)}-${phone.slice(2, 5)}-${phone.slice(5, 7)}`
            }
        }

        return result
    }
    
    const formatDni = (identifier: number) => formatNumber(identifier)

    const formatCuitOrCuil = (cuitOrCuil: number) => {
        const cuitOrCuilStr = cuitOrCuil.toString()
        const part1 = cuitOrCuilStr.slice(0, 2)
        const part2 = cuitOrCuilStr.slice(2, 4)
        const part3 = cuitOrCuilStr.slice(4, 7)
        const part4 = cuitOrCuilStr.slice(7, 10)
        const part5 = cuitOrCuilStr.slice(10, 11)
        return `${part1}-${part2}.${part3}.${part4}-${part5}`
    }

    const formatIdentifier = (identifier: number) =>
        identifierType(identifier) === IdentifierType.TAX_ID ? formatCuitOrCuil(identifier) : formatDni(identifier)

    const formatPhone = (value?: number) => {
        if (!isDefined(value)) return ''

        const phone = value!.toString()        
        let parts: string[] = []
        let currentPart = ''
        for (let i = phone.length - 1; i >= 0; i--) {
            currentPart = phone[i] + currentPart
            if (currentPart.length === 4) {
                parts.unshift(currentPart)
                currentPart = ''
            }
        }
        if (currentPart.length > 0) {
            parts.unshift(currentPart)
        }
        
        return parts.join('-')
    }

    const formatPlate = (plate: string) => {
        if (plate.length === 6) {
            return `${plate.slice(0, 3)}-${plate.slice(3, 6)}`
        } else if (plate.length === 7) {
            return `${plate.slice(0, 2)}-${plate.slice(2, 5)}-${plate.slice(5, 7)}`
        }
    }

    return {
        generateCode,
        generatePropCode,
        formatValue,
        formatNumber,
        formatPrice,
        formatDate,
        formatDateTime,
        formatPercentage,
        formatTable,
        formatCustomValue,
        formatDni,
        formatCuitOrCuil,
        formatIdentifier,
        formatPhone,
        formatPlate
    }
}
